import { useState, useEffect } from 'react';
import { systemSettingsApi, SystemSetting } from '../api/system-settings.api';
import {
  Container,
  Typography,
  Paper,
  Box,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import { Settings, Refresh } from '@mui/icons-material';
import { ActivityLogPanel } from '../components/admin/ActivityLogPanel';

export const AdminPage = () => {
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await systemSettingsApi.getAllSettings();
      setSettings(response.data.data || []);
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError('Failed to load system settings');
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaults = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await systemSettingsApi.initializeSettings();
      setSuccess('Default settings initialized successfully');
      fetchSettings();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Failed to initialize settings');
      } else {
        setError('Failed to initialize settings');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSetting = async (setting: SystemSetting) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const newValue = setting.value === 'true' ? 'false' : 'true';

      await systemSettingsApi.upsertSetting({
        key: setting.key,
        value: newValue,
        category: setting.category || undefined,
        isActive: setting.isActive,
      });

      setSuccess('Setting updated successfully');
      fetchSettings();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Failed to update setting');
      } else {
        setError('Failed to update setting');
      }
    } finally {
      setSaving(false);
    }
  };

  const getSettingLabel = (key: string): string => {
    const labels: Record<string, string> = {
      'features.news.enabled': 'News Feature',
      'features.competitions.enabled': 'Competitions Feature',
      'features.knowledge.enabled': 'Knowledge Center Feature',
      'features.snc.enabled': 'S&C Module Feature',
      'features.emailVerification.enabled': 'Email Verification on Registration',
    };
    return labels[key] || key;
  };

  const featureSettings = settings.filter((s) => s.category === 'features');

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Settings sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4">Administration</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Feature Toggles</Typography>
              <Button
                startIcon={<Refresh />}
                onClick={initializeDefaults}
                disabled={saving}
                size="small"
              >
                Initialize Defaults
              </Button>
            </Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Feature toggle changes are saved immediately, but users must refresh their browser to see the updated menu.
            </Alert>

            <Divider sx={{ mb: 2 }} />

            {featureSettings.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  No settings found. Click "Initialize Defaults" to create default settings.
                </Typography>
              </Box>
            ) : (
              <Box>
                {featureSettings.map((setting) => (
                  <Box key={setting.id} sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={setting.value === 'true'}
                          onChange={() => handleToggleSetting(setting)}
                          disabled={saving}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">{getSettingLabel(setting.key)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {setting.key}
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          <ActivityLogPanel />

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              System Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2">
                <strong>Total Settings:</strong> {settings.length}
              </Typography>
              <Typography variant="body2">
                <strong>Feature Settings:</strong> {featureSettings.length}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Feature toggles control the visibility of major application features. When a feature
                is disabled, it will be hidden from all user navigation menus.
              </Typography>
            </Box>
          </Paper>
        </>
      )}
    </Container>
  );
};
